import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { urlFor } from "~/client";
import { type SharedData } from "~/types";
import { router, usePage } from "@inertiajs/react";
import { BellRing, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Prominent, dismissible banner that surfaces the active company broadcast
 * shift reminder for the current user. Rendered once at the top of the main
 * workspace content area; renders nothing when there is no active reminder.
 */
export function ShiftReminderBanner() {
  const { t } = useTranslation("common");
  const { shiftReminder: rawShiftReminder } = usePage<SharedData>().props;
  // `shiftReminder` is an optional shared prop, so its inferred type is a union
  // with the framework's OptionalProp wrapper. Narrow to the resolved payload by
  // checking for a data key (same pattern as display-all-notifications).
  const shiftReminder = rawShiftReminder && "id" in rawShiftReminder ? rawShiftReminder : null;
  const [dismissed, setDismissed] = useState(false);

  // Reset the optimistic hide whenever a different broadcast becomes active so a
  // newly targeted reminder shows even if a previous one was dismissed.
  useEffect(() => {
    setDismissed(false);
  }, [shiftReminder?.id]);

  if (!shiftReminder || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    // Optimistically hide so the UI feels instant.
    setDismissed(true);

    // Persist the read, refreshing only the shiftReminder prop. If this fails
    // the banner stays hidden for this session and the server will re-show it
    // on the next full page load.
    router.post(
      urlFor("companyBroadcasts.read", { id: shiftReminder.id }),
      {},
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => router.reload({ only: ["shiftReminder"] }),
        // If the persist fails, restore the banner so it isn't hidden-but-unsaved
        // for the rest of the session.
        onError: () => setDismissed(false),
      },
    );
  };

  return (
    <div className="px-4 pt-4">
      <Alert className="border-primary/40 bg-primary/5 relative pr-12">
        <BellRing className="h-4 w-4" />
        {shiftReminder.title ? <AlertTitle>{shiftReminder.title}</AlertTitle> : null}
        <AlertDescription className="whitespace-pre-line">{shiftReminder.body}</AlertDescription>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 h-7 w-7"
          aria-label={t("shift_reminder.dismiss")}
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </Alert>
    </div>
  );
}
